import { useState } from "react";
import { Download, Paperclip } from "lucide-react";
import type { ChatMessageAttachment } from "../types";
import { MediaLightbox } from "./MediaLightbox";

interface MessageAttachmentsProps {
  attachments: ChatMessageAttachment[];
}

function isImage(contentType: string | undefined): boolean {
  return !!contentType && contentType.startsWith("image/");
}

/**
 * Renders multi-attachment payload above the text body.
 *  - 1 image: full-width
 *  - 2 images: 2-column grid
 *  - 3 images: 2-column with last spanning both
 *  - 4+ images: 2-column grid, all square thumbnails (+N overflow)
 *  - non-image files: rendered as one-line download chips beneath the images
 *
 * Klik w zdjęcie otwiera podgląd pełnoekranowy (strzałki przechodzą po zdjęciach
 * TEJ wiadomości) — nie nową kartę przeglądarki. Całą historię multimediów
 * rozmowy pokazuje panel „Multimedia i pliki" w nagłówku.
 */
export function MessageAttachments({ attachments }: MessageAttachmentsProps) {
  // Indeks zdjęcia otwartego w podglądzie. Hook stoi PRZED wczesnym returnem,
  // bo wiadomość bez załączników też przechodzi przez ten komponent.
  const [preview, setPreview] = useState<number | null>(null);
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => isImage(a.content_type));
  const files = attachments.filter((a) => !isImage(a.content_type));

  return (
    <div className="flex flex-col gap-1.5 mb-1">
      {images.length > 0 && <ImageGrid images={images} onOpen={setPreview} />}
      {preview !== null && (
        <MediaLightbox
          items={images}
          index={preview}
          onIndexChange={setPreview}
          onClose={() => setPreview(null)}
        />
      )}
      {files.map((f) => (
        <a
          key={f.id}
          href={f.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 truncate text-[12px] text-blue-700 underline"
        >
          <Paperclip size={12} className="flex-shrink-0" />
          <span className="truncate flex-1">{f.original_filename}</span>
          <Download size={11} className="flex-shrink-0" />
        </a>
      ))}
    </div>
  );
}

function ImageGrid({
  images,
  onOpen,
}: {
  images: ChatMessageAttachment[];
  onOpen: (index: number) => void;
}) {
  const tile = (img: ChatMessageAttachment, index: number, className: string,
                imgClassName: string, badge?: number) => (
    <button
      key={img.id}
      type="button"
      onClick={() => onOpen(index)}
      title={img.original_filename}
      className={`block rounded-md overflow-hidden relative ${className}`}
    >
      <img src={img.url ?? ""} alt={img.original_filename} className={imgClassName} />
      {!!badge && badge > 0 && (
        <span className="absolute inset-0 bg-black/50 text-white text-lg font-semibold flex items-center justify-center">
          +{badge}
        </span>
      )}
    </button>
  );

  if (images.length === 1) {
    return tile(images[0], 0, "w-full", "w-full max-h-72 object-contain");
  }
  if (images.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-1">
        {images.map((img, i) => tile(img, i, "", "w-full aspect-square object-cover"))}
      </div>
    );
  }
  if (images.length === 3) {
    return (
      <div className="grid grid-cols-2 gap-1">
        {tile(images[0], 0, "row-span-2 h-full", "w-full h-full object-cover")}
        {images.slice(1, 3).map((img, i) =>
          tile(img, i + 1, "", "w-full aspect-square object-cover"),
        )}
      </div>
    );
  }
  // 4+ — pokazujemy pierwsze 4, na czwartej kafelce licznik reszty; podgląd i tak
  // przechodzi po WSZYSTKICH zdjęciach wiadomości.
  const overflow = images.length - 4;
  return (
    <div className="grid grid-cols-2 gap-1">
      {images.slice(0, 4).map((img, i) =>
        tile(img, i, "", "w-full aspect-square object-cover", i === 3 ? overflow : 0),
      )}
    </div>
  );
}
